// The onboarding checklist hero card: bar progress + required steps + recommended.
import history from "../../../../../history";
import { Button } from "../../../../../components/UI";
import { PRIMARY, SECONDARY } from "../../../../../builders/buttonDesign.types";

import { StepGlyph, Icon } from "./glyphs";
import {
  OnboardingCardStyles,
  SparkleBox,
  ProgressBarStyles,
  IndicatorStyles,
  StepRowStyles,
  RecRowStyles,
  CTA_BUTTON_STYLE
} from "./onboarding-card.styles";

const Indicator = ({ status, glyph }) => {
  if (status === "done")
    return <IndicatorStyles $status="done">{Icon.check(17)}</IndicatorStyles>;
  if (status === "locked")
    return <IndicatorStyles $status="locked">{Icon.lock(15)}</IndicatorStyles>;
  if (status === "active" || status === "pending")
    return (
      <IndicatorStyles $status="active">
        <StepGlyph name={glyph} size={18} />
      </IndicatorStyles>
    );
  return (
    <IndicatorStyles $status="upcoming">
      <StepGlyph name={glyph} size={18} />
    </IndicatorStyles>
  );
};

const ProgressBar = ({ done, total }) => {
  const pct = total ? (done / total) * 100 : 0;
  return (
    <ProgressBarStyles $pct={pct}>
      <div className="ob-progress-label">
        <span className="ob-progress-caption">Setup progress</span>
        <span className="ob-progress-count">
          {done} of {total}
        </span>
      </div>
      <div className="ob-progress-track">
        <div className="ob-progress-fill" />
      </div>
    </ProgressBarStyles>
  );
};

const StepRow = ({ step, activeBranch, isActive, serviceMenuCount, isLast }) => {
  const { statusKind } = step;
  const active = statusKind === "active" || statusKind === "pending";
  const goToStep = () => history.push(step.to(activeBranch));

  return (
    <StepRowStyles
      $active={active}
      $locked={statusKind === "locked"}
      $done={statusKind === "done"}
    >
      {!isLast && <span className="ob-connector" />}
      <div className="ob-step-row">
        <div className="ob-indicator-wrap">
          <Indicator status={statusKind} glyph={step.glyph} />
        </div>
        <div className="ob-step-main">
          <div className="ob-step-head">
            <span className="ob-step-num">STEP {step.num}</span>
            <span className="ob-step-title">{step.title}</span>
            {statusKind === "done" && (
              <span className="ob-badge ob-badge-done">
                {Icon.check(13)} Done
              </span>
            )}
            {statusKind === "pending" && (
              <span className="ob-badge ob-badge-pending">
                {Icon.clock(12)} Under review
              </span>
            )}
          </div>

          {active && (
            <>
              <div className="ob-step-desc">
                {statusKind === "pending"
                  ? "Your details were submitted. Verification is usually approved within one business day — we'll notify you and unlock the next step."
                  : step.desc}
              </div>

              {step.checks && statusKind !== "pending" && (
                <div className="ob-checks">
                  {step.checks.map(check => {
                    const met =
                      check.k === "active" ? isActive : serviceMenuCount > 0;
                    return (
                      <div
                        key={check.k}
                        className={`ob-check${met ? " is-met" : ""}`}
                      >
                        <span className="ob-check-mark">{Icon.check(11)}</span>
                        {check.label}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="ob-cta-row">
                {statusKind === "pending" ? (
                  <span className="ob-cta-hint">
                    {Icon.clock(13)} Awaiting approval
                  </span>
                ) : (
                  <>
                    <Button
                      buttonDesignType={PRIMARY}
                      onClick={goToStep}
                      style={CTA_BUTTON_STYLE}
                    >
                      {step.cta}
                      {Icon.arrowOut(15)}
                    </Button>
                    <span className="ob-cta-hint">Opens {step.route}</span>
                  </>
                )}
              </div>
            </>
          )}

          {statusKind === "locked" && (
            <div className="ob-locked-hint">
              Complete the previous step to unlock.
            </div>
          )}
        </div>
      </div>
    </StepRowStyles>
  );
};

export const RecRow = ({ step, activeBranch }) => (
  <RecRowStyles>
    <Indicator status={step.done ? "done" : "upcoming"} glyph={step.glyph} />
    <div className="ob-rec-main">
      <div className="ob-rec-head">
        <span className="ob-rec-name">{step.title}</span>
        {step.done ? (
          <span className="ob-rec-added">{Icon.check(12)} Added</span>
        ) : (
          <span className="ob-rec-optional">Optional</span>
        )}
      </div>
      <div className="ob-rec-desc">{step.desc}</div>
    </div>
    {!step.done && (
      <Button
        buttonDesignType={SECONDARY}
        onClick={() => history.push(step.to(activeBranch))}
        style={CTA_BUTTON_STYLE}
      >
        {step.cta}
        {Icon.arrowOut(14)}
      </Button>
    )}
  </RecRowStyles>
);

const OnboardingCard = ({ status, onMinimize }) => {
  const {
    steps,
    recommended,
    requiredDone,
    requiredTotal,
    branchName,
    activeBranch,
    isActive,
    serviceMenuCount,
    hasBranch
  } = status;

  return (
    <OnboardingCardStyles>
      <div className="ob-header">
        <div className="ob-heading">
          <SparkleBox>{Icon.sparkle(20)}</SparkleBox>
          <div>
            <div className="ob-title">Finish setting up your shop</div>
            <div className="ob-desc">
              Complete these {requiredTotal} steps so {branchName} can start
              receiving service requests.
            </div>
          </div>
        </div>
        <div className="ob-header-right">
          <ProgressBar done={requiredDone} total={requiredTotal} />
          <button
            type="button"
            className="ob-minimize"
            title="Minimize"
            onClick={onMinimize}
          >
            {Icon.chevronDown(16)}
          </button>
        </div>
      </div>

      <div className="ob-steps">
        {steps.map((step, index) => (
          <StepRow
            key={step.id}
            step={step}
            activeBranch={activeBranch}
            isActive={isActive}
            serviceMenuCount={serviceMenuCount}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>

      {hasBranch && (
        <div className="ob-recommended">
          <div className="ob-rec-heading">
            <span className="ob-rec-title">Recommended next</span>
            <span className="ob-rec-note">
              · not required to go live, but helps you win more work
            </span>
          </div>
          {recommended.map(step => (
            <RecRow key={step.id} step={step} activeBranch={activeBranch} />
          ))}
        </div>
      )}
    </OnboardingCardStyles>
  );
};

export default OnboardingCard;
